/*
 * How a listing is let, and the words for where it stands. Kept in one place so
 * the listing form, the cards, the listing page and the landlord's list all say
 * the same thing.
 */

export const LETTING = {
  whole: {
    label: 'A single house or flat',
    short: 'Whole house',
    hint: 'Let as one, to one household, at one rent.',
    unit: null,
    units: null
  },
  units: {
    label: 'An apartment building',
    short: 'Apartment building',
    hint: 'Several apartments in one complex - Apt 1, Apt 2 - each let on its own, at its own rent.',
    unit: 'apartment',
    units: 'apartments'
  },
  rooms: {
    label: 'Rooms in a house',
    short: 'Rooms',
    hint: 'A house let room by room, each room to a different tenant.',
    unit: 'room',
    units: 'rooms'
  }
};

export const lettingOf = (property) => LETTING[property?.letting] || LETTING.whole;
export const isMultiUnit = (property) => property?.letting === 'units' || property?.letting === 'rooms';

export const AVAILABILITY_LABEL = {
  available: 'Available',
  pending: 'Pending',
  occupied: 'Rented',
  unavailable: 'Under repair'
};

/** "3 of 8 apartments available", or null for a whole house. */
export const unitsSummary = (property) => {
  const counts = property?.unitCounts;
  if (!isMultiUnit(property) || !counts?.total) return null;
  const { units } = lettingOf(property);
  return counts.available > 0
    ? `${counts.available} of ${counts.total} ${units} available`
    : `All ${counts.total} ${units} taken`;
};

// ---------------------------------------------------------------- for sale

export const isForSale = (property) => property?.listingKind === 'sale';

// A sale reads the same three states differently: for sale, under offer, sold.
const SALE_BADGE = {
  available: { status: 'available', label: 'For sale' },
  pending: { status: 'pending', label: 'Under offer' },
  occupied: { status: 'sold', label: 'Sold' }
};

/** The badge a listing wears: its status, in the words that fit what it is. */
export const statusBadgeFor = (property) => {
  // A listing that has been public before is off the market, not a draft.
  if (property?.status === 'draft') {
    return property.offMarket || property.everListed ? { status: 'withdrawn', label: 'Off the market' } : { status: 'draft', label: 'Draft' };
  }
  const availability = property?.availability || (property?.status === 'rented' ? 'occupied' : property?.status || 'available');
  if (isForSale(property)) return SALE_BADGE[availability] || SALE_BADGE.available;
  return { status: availability, label: AVAILABILITY_LABEL[availability] || availability };
};

/** Plot sizes as people say them: square metres, or hectares once it is big. */
export const formatPlot = (sqm) => {
  if (!sqm) return null;
  if (sqm >= 10000) return `${(sqm / 10000).toLocaleString('en', { maximumFractionDigits: 2 })} ha`;
  return `${sqm.toLocaleString('en').replace(/,/g, ' ')} m2`;
};
